import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Trash2 } from 'lucide-react';
import { getErrorMessage } from '@/lib/errors';
import { formatDate } from '@/lib/dateFormat';
import { idManagementService, type IdCategory } from '@/services/id-management.service';
import { CategoryIcon } from '@/components/id-management/CategoryIcon';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { IdCategoryFormDialog } from './IdCategoryFormDialog';

type FormState = { category?: IdCategory } | null;

export function IdCategoriesTab() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(null);
  const [deleteTarget, setDeleteTarget] = useState<IdCategory | null>(null);

  const categoriesQuery = useQuery({
    queryKey: ['id-management', 'categories'],
    queryFn: () => idManagementService.listCategories(),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => idManagementService.deleteCategory(id),
    onSuccess: () => {
      toast.success('Category deleted');
      void queryClient.invalidateQueries({ queryKey: ['id-management', 'categories'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const columns: Column<IdCategory>[] = [
    {
      key: 'name',
      header: 'Category',
      cell: (row) => (
        <span className="flex items-center gap-2.5">
          <CategoryIcon name={row.name} iconPath={row.icon_path} className="h-8 w-8" />
          <span className="min-w-0 font-medium text-foreground [overflow-wrap:anywhere]">{row.name}</span>
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      cell: (row) => <span className="text-muted-foreground">{formatDate(row.created_at)}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'w-28 text-right',
      cell: (row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8" aria-label={`Edit ${row.name}`} onClick={() => setForm({ category: row })}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8" aria-label={`Delete ${row.name}`} onClick={() => setDeleteTarget(row)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setForm({})}>+ ID Category</Button>
      </div>

      <DataTable
        columns={columns}
        rows={categoriesQuery.data ?? []}
        rowKey={(row) => row.id}
        loading={categoriesQuery.isLoading}
        loadingRows={4}
        error={categoriesQuery.isError}
        onRetry={() => void categoriesQuery.refetch()}
        empty="No ID categories yet. Add one such as Google, Microsoft or Apple to get started."
      />

      {form && <IdCategoryFormDialog category={form.category} onClose={() => setForm(null)} />}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete ID category?"
        description={deleteTarget ? `"${deleteTarget.name}" will be removed. A category that still has saved IDs can't be deleted.` : undefined}
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) deleteMut.mutate(deleteTarget.id); }}
      />
    </>
  );
}
